import api from "services/api";
import axios, { Method } from "axios";
import phoneMask from "utils/phoneMask";
import { toast } from "react-toastify";

export const requestGetAccessToken = async (): Promise<any> => {
  const header = {
    "Content-Type": "application/json",
    "Accept-Language": "pt-BR",
    Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
  };
  const parameters = {
    method: "POST" as Method,
    headers: header,
  };
  const body = {
    grant_type: "client_credentials",
  };

  let accessToken = "";

  try {
    const response = await api.post(
      "industrial/customer/v1/users/authentications",
      body,
      parameters
    );
    accessToken = response.data.access_token;
  } catch (error) {
    const erro = error.response;
    if (erro.status === 400) {
      toast.error("Autenticação inválida");
    }
    if (erro) {
      const { message } = error.response.data;
      if (error.response.status !== 401) {
        toast.error(message);
      }
    } else if (error.response.status !== 401) {
      toast.error("Ocorreu um erro interno na aplicação");
    }
  }

  return accessToken;
};

export const requestOauth = async (
  login,
  password,
  token,
  accessToken,
  dispatch,
  callBack
): Promise<any> => {
  const mfaToken = localStorage.getItem("@UltragazEmpresarial:X-Mfa-Hash")
    ? localStorage.getItem("@UltragazEmpresarial:X-Mfa-Hash")
    : null;

  const req = axios.create({
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Accept-Language": "pt-BR",
      client_id: process.env.REACT_APP_CLIENT_ID,
      "MFA-Enabled": true,
      ...(mfaToken && { "x-mfa-hash": mfaToken }),
    },
    baseURL: process.env.REACT_APP_API_URL,
  });

  const headers = {
    access_token: accessToken,
    Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
  };
  const body = {
    grant_type: "password",
    login,
    username: login,
    password,
    ...(!mfaToken && token && { token }),
  };
  const response = await req.post(
    `industrial/customer/v1/users/authentications`,
    body,
    { headers }
  );

  const { data } = response;
  if (response.status === 201) {
    dispatch(callBack(data));
    const tokenMFA = response.headers["x-mfa-hash"];

    localStorage.setItem("@UltragazEmpresarial:accessToken", data.access_token);
    localStorage.setItem(
      "@UltragazEmpresarial:refreshToken",
      data.refresh_token
    );
    if (tokenMFA) {
      localStorage.setItem("@UltragazEmpresarial:X-Mfa-Hash", tokenMFA);
    }
    return data;
  }

  return null;
};

export const requestGetUser = async (
  login,
  accessToken,
  dispatch,
  callBack
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    access_token: accessToken,
  };
  const params = {
    login,
    "is-active": true,
  };
  const response = await api.get(`industrial/customer/v1/users`, {
    headers,
    params,
  });

  if (response.data.length > 0) {
    const data = response.data[0];
    const { userId } = data;
    localStorage.setItem("@UltragazEmpresarial:loginEmail", login);
    dispatch(callBack(userId));
    return data;
  }
  return response.data;
};

export const requestGetCustomersWithDocument = async (
  accessToken,
  document,
  dispatch,
  registerValues,
  registerValuesMembership
): Promise<any> => {
  const headers = {
    access_token: accessToken,
  };
  const params = {
    document,
    type: "MI",
    _limit: 1,
    _offset: 0,
  };
  const response = await api.get(`industrial/customer/v1/customers`, {
    headers,
    params,
  });

  localStorage.setItem("@UltragazEmpresarial:accessToken", accessToken);

  dispatch(
    registerValues({
      siteId: response.data[0]?.customerAddresses[0]?.siteId,
    })
  );

  if (response?.data[0]?.id) {
    dispatch(registerValuesMembership({ awsCustomerId: response.data[0].id }));
    // activating digital way
    if (response.data[0]?.customerAddresses.length <= 2) {
      dispatch(
        registerValues({
          unitId: response.data[0]?.customerAddresses[0]?.unitId,
        })
      );
    }
  }

  return response.data[0];
};

const requestGetPhoneNumber = async (
  accessToken,
  location,
  dispatch,
  registerValues
): Promise<any> => {
  const headers = {
    access_token: accessToken,
  };
  const params = {
    contactType: "PHONE",
  };

  const response = await api.get(`${location}/contacts`, {
    headers,
    params,
  });
  const { data } = response;
  dispatch(registerValues({ phones: data }));
  return response;
};

export const requestPostUser = async (
  accessToken,
  _,
  document,
  dispatch,
  registerLocation,
  toggleStep,
  registerValues,
  history
): Promise<any> => {
  const body = {
    document,
  };
  const headers = {
    access_token: accessToken,
  };
  const response = await api.post("industrial/customer/v1/users", body, {
    headers,
  });

  const { location } = response.headers;

  const responsePhone = await requestGetPhoneNumber(
    accessToken,
    location,
    dispatch,
    registerValues
  );

  dispatch(registerLocation(location));
  const { data } = responsePhone;
  if (!data.length) {
    dispatch(toggleStep(2));
  } else {
    dispatch(toggleStep(2));
  }
  history.push("/primeiro-acesso-mi");
};

export const requestGetCustomersWithUserId = async (
  userId,
  accessToken
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    access_token: accessToken,
  };
  const params = {
    status: "ATIVO",
    type: "MI",
    address_type: "SHIP_TO",
    "user-id": userId,
    _offset: 0,
    _limit: 100,
  };

  const response = await api.get(`industrial/customer/v1/customers`, {
    headers,
    params,
  });

  if (response.data.length > 0) {
    const data = response.data[0];
    return data;
  }

  return response.data;
};

export const requestGetCustomer = async (siteId, token): Promise<any> => {
  const header = {
    "Content-Type": "application/json",
    access_token: token,
  };

  const parameters = {
    method: "GET" as Method,
    headers: header,
    type: "MI",
    _expand: "CONTACT",
  };

  api
    .get("industrial/customer/v1/customers/me", parameters)
    .then((res) => {
      const {
        data: { customerAddresses },
      } = res;

      const [address] = customerAddresses.filter(
        (customerAddress) =>
          customerAddress.addressType === "SHIP_TO" &&
          customerAddress.siteId === Number(siteId)
      );

      if (!address) {
        return;
      }

      const { contacts } = address;

      if (contacts.length > 0) {
        const [phone] = contacts.filter(
          (contact) =>
            contact.contactType === "PHONE" && contact.isValidated === true
        );

        if (!phone) {
          return;
        }
        const contactPhone = phoneMask(phone.contact);
        localStorage.setItem("@UltragazEmpresarial:phone", contactPhone);
      }
    })
    .catch((err) => {
      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    });

  return "";
};

export const updateUserCount = async (login, accessToken): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    access_token: accessToken,
  };
  const params = {
    login,
    "is-active": true,
  };
  const response = await api.get(`industrial/customer/v1/users`, {
    headers,
    params,
  });

  if (response.data.length > 0) {
    const data = response.data[0];
    const { loginCount } = data;
    localStorage.setItem("@UltragazEmpresarial:loginCount", loginCount || "");
    return data;
  }

  return null;
};

export const resetLocalStorage = (): void => {
  localStorage.removeItem("@UltragazEmpresarial:city");
  localStorage.removeItem("@UltragazEmpresarial:stateCode");
  localStorage.removeItem("@UltragazEmpresarial:customerName");
  localStorage.removeItem("@UltragazEmpresarial:customerSiteCode");
  localStorage.removeItem("@UltragazEmpresarial:customerSiteId");
  localStorage.removeItem("@UltragazEmpresarial:customerCode");
  localStorage.removeItem("@UltragazEmpresarial:customerId");
  localStorage.removeItem("@UltragazEmpresarial:customerName");
  localStorage.removeItem("@UltragazEmpresarial:userCustomerRelationshipId");
  localStorage.removeItem("@UltragazEmpresarial:userName");
  localStorage.removeItem("@UltragazEmpresarial:customerType");
  localStorage.removeItem("@UltragazEmpresarial:userIdMFA");
};
